import { useQuery } from '@apollo/client'
import { Autocomplete, Box, TextField, Typography } from '@mui/material'
import { GetCandidatesByNameQuery } from '__generated__/graphql'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { getFullName } from 'shared/utils'
import { GetCandidatesByName } from './CandidatesAutocomplete.gql'
import { CandidatesAutocompleteProps } from './CandidatesAutocompleteProps'

type CandidateOption =
    GetCandidatesByNameQuery['candidatesByName']['data'][number]

const getSecondaryText = (option: CandidateOption): string => {
    const email = option.user?.email
    const company = option.currentWorkExperience?.company?.value

    if (email && company) {
        return `${email} | ${company}`
    }
    if (email) {
        return email
    }
    if (company) {
        return company
    }
    return ''
}

const LIMIT = 25

const CandidatesAutocompleteSingle: FC<CandidatesAutocompleteProps<false>> = ({
    error,
    helperText,
    size,
    placeholder,
    label = 'Candidate',
    showSecondaryText = true,
    ...props
}) => {
    const [name, setName] = useState('')
    const [debounceName, setDebouncedName] = useState(name)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedName(name)
            nextPageToFetch.current = 2
        }, 500)

        return () => {
            clearTimeout(handler)
        }
    }, [name])

    const { data, fetchMore, loading } = useQuery(GetCandidatesByName, {
        variables: { name: debounceName, page: 1, limit: LIMIT },
        fetchPolicy: 'network-only',
        skip: !open,
    })

    const loadMoreData = useCallback(() => {
        fetchMore({
            variables: {
                page: nextPageToFetch.current,
                limit: LIMIT,
                name: debounceName,
            },
            updateQuery(
                previousData,
                { fetchMoreResult, variables: { page, limit } }
            ) {
                if (
                    !previousData?.candidatesByName?.data ||
                    !fetchMoreResult?.candidatesByName?.data
                )
                    return fetchMoreResult ?? previousData
                const updatedData = Array.from(
                    previousData.candidatesByName.data
                )
                const pageLimit =
                    previousData.candidatesByName.pageInfo.pageTotal
                nextPageToFetch.current =
                    page + 1 <= pageLimit ? page + 1 : page
                for (
                    let i = 0;
                    i < fetchMoreResult.candidatesByName.data.length;
                    i++
                ) {
                    updatedData[(page - 1) * limit + i] =
                        fetchMoreResult.candidatesByName.data[i]
                }
                const newCache = {
                    ...previousData,
                    candidatesByName: {
                        ...fetchMoreResult.candidatesByName,
                        data: updatedData,
                    },
                }
                if (
                    fetchMoreResult?.candidatesByName?.pageInfo.itemTotal &&
                    fetchMoreResult.candidatesByName.pageInfo.itemTotal ===
                        updatedData.length
                ) {
                    observer.current?.disconnect()
                }
                return newCache
            },
        })
    }, [fetchMore, debounceName])

    const observerCallback = useCallback(
        (entries) => {
            if (entries[0].isIntersecting) {
                loadMoreData()
            }
        },
        [loadMoreData]
    )

    const observedElementRef = useRef<HTMLLIElement | null>(null)
    const nextPageToFetch = useRef<number>(2)
    const observer = useRef<IntersectionObserver>(
        new IntersectionObserver(observerCallback)
    )

    useEffect(() => {
        observer.current = new IntersectionObserver(observerCallback)
        if (observedElementRef.current) {
            observer.current?.observe(observedElementRef.current)
        }

        return () => {
            if (observer.current) observer.current.disconnect()
        }
    }, [observerCallback, open, data?.candidatesByName.data])

    return (
        <Autocomplete
            fullWidth
            filterOptions={(x) => x}
            disablePortal
            getOptionLabel={(option) =>
                option.user ? getFullName(option.user) : ''
            }
            loading={loading}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            open={open}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option, state, ownerState) => {
                const index = state.index
                const totalOptions = ownerState.options.length
                const isObserver = index === totalOptions - 1
                const secondaryText = showSecondaryText
                    ? getSecondaryText(option)
                    : ''
                const nameText = option.user ? getFullName(option.user) : ''
                return (
                    <li
                        {...props}
                        key={option.id}
                        ref={isObserver ? observedElementRef : null}
                    >
                        {showSecondaryText && secondaryText ? (
                            <Box>
                                <Typography variant="body1">
                                    {nameText}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {secondaryText}
                                </Typography>
                            </Box>
                        ) : (
                            nameText
                        )}
                    </li>
                )
            }}
            onInputChange={(e, value, reason) => {
                if (reason !== 'reset') setName(value)
            }}
            {...props}
            options={data?.candidatesByName.data || []}
            renderInput={(params) => (
                <TextField
                    {...params}
                    size={size}
                    label={label}
                    variant="outlined"
                    error={error}
                    helperText={helperText}
                    placeholder={placeholder}
                />
            )}
        />
    )
}

export default CandidatesAutocompleteSingle
